import { type PostHog } from 'posthog-js';
import { isOneOf } from '../utils/types';
import { captureException } from '@sentry/react';

// Use "location?" for not failing unit tests
const enableAnalytics = isOneOf(globalThis.location?.hostname, ['bestill.rikstv.no', 'bestill.uat.rikstv.no']);
const logPrefix = enableAnalytics ? '[posthog]' : '[posthog-disabled]';

/**
 * Lazy-load posthog client only when events are used to avoid having it part of every bundle.
 * That way it costs us nothing to have it available in the codebase.
 */
let _posthogPromise: Promise<PostHog> | null = null;
const lazyLoadClient = async () => {
  if (_posthogPromise) {
    return _posthogPromise;
  }
  // Allow max 500ms to wait for posthog loaded state since we might prevent rendering otherwise
  _posthogPromise = new Promise((resolve) => {
    import('posthog-js')
      .then(({ posthog }) => {
        posthog.init('phc_kqixdPiJcf3OETOVmnip0rBeBZz6CAZya64q7uN2ZIq', {
          api_host: 'https://eu.i.posthog.com',
          persistence: 'localStorage',
          // disable all auto capture
          autocapture: false,
          capture_pageview: false,
          capture_performance: false,
          capture_heatmaps: false,
          capture_dead_clicks: false,
          capture_pageleave: false,
          enable_recording_console_log: false,
          advanced_disable_feature_flags: true,
          before_send: (payload) => {
            console.debug(
              `${logPrefix} before_send: "${payload?.event}":`,
              Object.fromEntries(
                Object.entries(payload?.properties ?? {}).filter(
                  ([key]) => !key.startsWith('$') && !['token', 'distinct_id'].includes(key),
                ),
              ),
            );
            return enableAnalytics ? payload : null;
          },
        });
        resolve(posthog);
      })
      .catch((e) => {
        captureException(new Error('Failed to load posthog script', { cause: e }), { fingerprint: ['posthog-load-failure'] });
        _posthogPromise = null;
      });
  });
  return _posthogPromise;
};

// Used to assert analytics events in e2e tests
const posthogEventLog = (globalThis as any)?.posthogEventLog as undefined | Array<any>;

const _captureEvent: PostHog['capture'] = (event_name, properties, options) => {
  // Next line used in e2e tests only
  if (posthogEventLog) {
    posthogEventLog.push({ event: event_name, properties });
  }
  lazyLoadClient().then((posthog) => {
    posthog.capture(event_name, properties, options);
  });
  return undefined;
};

export const captureUXEvent = (event: string, properties: Record<string, any> = {}) => {
  try {
    _captureEvent(event, properties);
  } catch {
    /* om nom nom */
  }
};

/**
 * Makes sure each recurring event is at least one hour apart
 */
export const captureUXEventOnce = (event: string, properties?: Record<string, any>) => {
  const key = `rtv_event_${event}`;
  const lastSent = sessionStorage.getItem(key);
  if (lastSent) {
    // return if last event was less than one hour ago
    if (Number.parseInt(lastSent) + 3600_000 < Date.now()) {
      return;
    }
  }
  sessionStorage.setItem(key, Date.now().toString());
  captureUXEvent(event, properties);
};
